<template>
  <div>
    <b-container class="metric-info">
      <b-row>
        <b-col md="12">
          <h4>{{ metric.label }}</h4>
          <p class="mt-1" v-if="metric.text">{{ metric.text }}</p>
        </b-col>
      </b-row>
      <b-row class="pt-3">
        <b-col fluid class="metric-score">
          <Overall
            :score="metric.score"
            :reliabilityRate="metric.reliability_rate"
            class="pt-3"
          />
          <HistoricChart :history="metric.history" />
        </b-col>
      </b-row>
      <b-row class="pt-3">
        <b-col fluid>
          <div
            class="metric-questions"
            v-for="(question, index) in metric.questions"
            :key="question.question_id"
          >
            <b-row v-if="index === 0" class="titles">
              <b-col md="10">
                <small class="d-none d-md-block">
                  {{ labels.reliabilityRate.questions }}
                </small>
              </b-col>
              <b-col md="2">
                <small class="d-none d-md-block text-center">
                  {{ labels.reliabilityRate.title1 }}
                  <br />
                  {{ labels.reliabilityRate.title2 }}
                </small>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="10" class="question-item pb-1">
                <small>{{ question.text }}</small>
                <img
                  v-if="question.diversity"
                  class="smart-metric-icon"
                  :title="labels.types.diversity"
                  :src="getSmartMetricImgUrl('diversity')"
                />
                <img
                  v-if="question.psychologicalSafety"
                  class="smart-metric-icon"
                  :title="labels.types.psychologicalSafety"
                  :src="getSmartMetricImgUrl('psychologicalSafety')"
                />
                <img
                  v-if="question.burnout && isAdmin"
                  class="smart-metric-icon"
                  :title="labels.types.burnout"
                  :src="getSmartMetricImgUrl('burnout')"
                />
                <img
                  v-if="question.psychosocialRisk"
                  class="smart-metric-icon"
                  :title="labels.types.psychosocialRisk"
                  :src="getSmartMetricImgUrl('psychosocialRisk')"
                />
                <b-badge
                  pill
                  v-if="question.status === 'active-new'"
                  class="ml-1 question-status"
                  variant="warning"
                >
                  {{ labels.questions.questionNew }}
                </b-badge>
                <b-badge
                  pill
                  v-if="question.status === 'active-updated'"
                  class="ml-1 question-status"
                  variant="blue"
                >
                  {{ labels.questions.questionUpdated }}
                </b-badge>
                <b-badge
                  pill
                  v-if="question.status === 'deprecated'"
                  class="ml-1 question-status"
                  variant="gray01"
                >
                  {{ labels.questions.questionDeprecated }}
                </b-badge>
                <b-progress :max="100" show-value animated class="mb-1">
                  <b-progress-bar
                    :id="'question-info' + question.question_id"
                    :value="getQuestionValue(question.frequencies, null)"
                    variant="info"
                  >
                    {{ getQuestionValue(question.frequencies, null) }}%
                  </b-progress-bar>
                  <b-progress-bar
                    :id="'question-red' + question.question_id"
                    :value="getQuestionValue(question.frequencies, -2)"
                    variant="red10"
                  >
                    {{ getQuestionValue(question.frequencies, -2) }}%
                  </b-progress-bar>
                  <b-progress-bar
                    :id="'question-red02' + question.question_id"
                    :value="getQuestionValue(question.frequencies, -1)"
                    variant="red09"
                  >
                    {{ getQuestionValue(question.frequencies, -1) }}%
                  </b-progress-bar>
                  <b-progress-bar
                    :id="'question-warning' + question.question_id"
                    :value="getQuestionValue(question.frequencies, 0)"
                    variant="warning"
                  >
                    {{ getQuestionValue(question.frequencies, 0) }}%
                  </b-progress-bar>
                  <b-progress-bar
                    :id="'question-success' + question.question_id"
                    :value="getQuestionValue(question.frequencies, 1)"
                    variant="green"
                  >
                    {{ getQuestionValue(question.frequencies, 1) }}%
                  </b-progress-bar>
                  <b-progress-bar
                    :id="'question-green' + question.question_id"
                    :value="getQuestionValue(question.frequencies, 2)"
                    variant="success"
                  >
                    {{ getQuestionValue(question.frequencies, 2) }}%
                  </b-progress-bar>
                  <b-progress-bar
                    v-if="
                      question.frequencies === null ||
                        question.frequencies.length === 0
                    "
                    :value="100"
                    variant="gray02"
                  >
                    {{ labels.questions.none }}
                  </b-progress-bar>
                </b-progress>
                <b-tooltip :target="'question-info' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.questions.skip }}
                    {{ getQuestionValue(question.frequencies, null) }}%
                  </div>
                </b-tooltip>
                <b-tooltip :target="'question-red' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.questions.veryUnhappy }}
                    {{ getQuestionValue(question.frequencies, -2) }}%
                  </div>
                </b-tooltip>
                <b-tooltip :target="'question-red02' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.questions.unhappy }}
                    {{ getQuestionValue(question.frequencies, -1) }}%
                  </div>
                </b-tooltip>
                <b-tooltip :target="'question-warning' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.questions.neutral }}
                    {{ getQuestionValue(question.frequencies, 0) }}%
                  </div>
                </b-tooltip>
                <b-tooltip :target="'question-success' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.questions.happy }}
                    {{ getQuestionValue(question.frequencies, 1) }}%
                  </div>
                </b-tooltip>
                <b-tooltip :target="'question-green' + question.question_id">
                  <div class="tooltip-info">
                    {{ labels.questions.veryHappy }}
                    {{ getQuestionValue(question.frequencies, 2) }}%
                  </div>
                </b-tooltip>
              </b-col>
              <b-col
                md="2"
                class="d-none d-md-block reliability-rate text-center mt-3"
              >
                <small>
                  <span :class="question.reliability_rate">
                    {{ labels.reliabilityRate[question.reliability_rate] }}
                  </span>
                </small>
              </b-col>
              <b-col md="12" class="d-block d-md-none reliability-rate pb-3">
                <small>
                  {{ labels.reliabilityRate.title1 }}
                  {{ labels.reliabilityRate.title2 }}
                  <span :class="question.reliability_rate">
                    {{ labels.reliabilityRate[question.reliability_rate] }}
                  </span>
                </small>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
      <hr />
      <b-row v-if="index + 1 === metricsSize" class="pb-2">
        <b-col>
          <h4>{{ labels.questions.subtitle }}</h4>
          <p class="mt-3 text-gray04">
            {{ labels.reliabilityRate.questions }}
          </p>
          <b-row>
            <b-col>
              <b-progress :max="100" show-value class="mt-3">
                <b-progress-bar :value="100" variant="gray02" class="subtitle">
                  {{ labels.questions.none }}
                </b-progress-bar>
              </b-progress>
            </b-col>
            <b-col>
              <b-progress :max="100" show-value class="mt-3">
                <b-progress-bar :value="100" variant="info" class="subtitle">
                  {{ labels.questions.skip }}
                </b-progress-bar>
              </b-progress>
            </b-col>
            <b-col>
              <b-progress :max="100" show-value class="mt-3">
                <b-progress-bar :value="100" variant="red10" class="subtitle">
                  {{ labels.questions.veryUnhappy }}
                </b-progress-bar>
              </b-progress>
            </b-col>
            <b-col>
              <b-progress :max="100" show-value class="mt-3">
                <b-progress-bar :value="100" variant="red09" class="subtitle">
                  {{ labels.questions.unhappy }}
                </b-progress-bar>
              </b-progress>
            </b-col>
            <b-col>
              <b-progress :max="100" show-value class="mt-3">
                <b-progress-bar :value="100" variant="warning" class="subtitle">
                  {{ labels.questions.neutral }}
                </b-progress-bar>
              </b-progress>
            </b-col>
            <b-col>
              <b-progress :max="100" show-value class="mt-3">
                <b-progress-bar :value="100" variant="green" class="subtitle">
                  {{ labels.questions.happy }}
                </b-progress-bar>
              </b-progress>
            </b-col>
            <b-col>
              <b-progress :max="100" show-value class="mt-3">
                <b-progress-bar :value="100" variant="success" class="subtitle">
                  {{ labels.questions.veryHappy }}
                </b-progress-bar>
              </b-progress>
            </b-col>
          </b-row>
          <p class="mt-4 text-gray04">
            {{ labels.reliabilityRate.title1 }}
            {{ labels.reliabilityRate.title2 }}
          </p>
          <b-row>
            <b-col>
              <b-progress
                class="mt-3"
                :max="100"
                show-value
                :title="
                  `${labels.reliabilityRate.veryLow}: 0% ~ 20% ${labels.reliabilityRate.responders}`
                "
              >
                <b-progress-bar :value="100" variant="red10">
                  {{ labels.reliabilityRate.veryLow }}
                </b-progress-bar>
              </b-progress>
              <p class="text-center">
                {{ `0% ~ 20% ${labels.reliabilityRate.responders}` }}
              </p>
            </b-col>
            <b-col>
              <b-progress
                class="mt-3"
                :max="100"
                show-value
                :title="
                  `${labels.reliabilityRate.low}: 20% ~ 40% ${labels.reliabilityRate.responders}`
                "
              >
                <b-progress-bar :value="100" variant="red09">
                  {{ labels.reliabilityRate.low }}
                </b-progress-bar>
              </b-progress>
              <p class="text-center">
                {{ `20% ~ 40% ${labels.reliabilityRate.responders}` }}
              </p>
            </b-col>
            <b-col>
              <b-progress
                class="mt-3"
                :max="100"
                show-value
                :title="
                  `${labels.reliabilityRate.medium}: 40% ~ 60% ${labels.reliabilityRate.responders}`
                "
              >
                <b-progress-bar :value="100" variant="warning">
                  {{ labels.reliabilityRate.medium }}
                </b-progress-bar>
              </b-progress>
              <p class="text-center">
                {{ `40% ~ 60% ${labels.reliabilityRate.responders}` }}
              </p>
            </b-col>
            <b-col>
              <b-progress
                class="mt-3"
                :max="100"
                show-value
                :title="
                  `${labels.reliabilityRate.high}: 60% ~ 80% ${labels.reliabilityRate.responders}`
                "
              >
                <b-progress-bar :value="100" variant="green">
                  {{ labels.reliabilityRate.high }}
                </b-progress-bar>
              </b-progress>
              <p class="text-center">
                {{ `60% ~ 80% ${labels.reliabilityRate.responders}` }}
              </p>
            </b-col>
            <b-col>
              <b-progress
                class="mt-3"
                :max="100"
                show-value
                :title="
                  `${labels.reliabilityRate.veryHigh}: 80% ~ 100% ${labels.reliabilityRate.responders}`
                "
              >
                <b-progress-bar :value="100" variant="success">
                  {{ labels.reliabilityRate.veryHigh }}
                </b-progress-bar>
              </b-progress>
              <p class="text-center">
                {{ `80% ~ 100% ${labels.reliabilityRate.responders}` }}
              </p>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import Overall from '@/components/Charts/Overall.vue'
import HistoricChart from '@/components/Reports/HistoricChart.vue'
import { mapGetters } from 'vuex'
import { isAdmin } from '@/service/user'

const props = {
  index: {
    require: true,
    type: Number,
  },
  metricsSize: {
    require: true,
    type: Number,
  },
  metric: {
    require: true,
    type: Object,
  },
}

export default {
  components: { Overall, HistoricChart },
  props: props,
  computed: {
    ...mapGetters({
      resources: 'resources',
    }),
    labels() {
      return {
        ...this.resources.page.reports.engagement.smartMetrics,
        ...this.resources.page.reports.engagement.metric,
      }
    },
    isAdmin() {
      return isAdmin()
    },
  },
  methods: {
    getQuestionValue(frequencies, score) {
      if (!frequencies) return 0
      const item = frequencies.find(f => f.score === score)
      const value = item ? item.frequency * 100 : 0
      return Number(value.toFixed(2))
    },
    getSmartMetricImgUrl(metricId) {
      return `https://static.teamculture.com.br/images/metrics/${metricId}.svg`
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.metric-info {
  margin: 0.5rem 0.6rem 0rem 0rem;
  width: 100%;

  .smart-metric-icon {
    border-radius: 50%;
    width: 26px;
    height: 26px;
    margin: 5px;
  }

  .metric-questions {
    color: map-get($theme-colors, 'gray02');

    .question-status {
      font-weight: normal;
    }
  }

  .metric-score {
    display: flex;
    justify-content: space-around;
    align-items: center;
  }

  h4 {
    border-radius: 30px;
    color: map-get($theme-colors, 'gray04');
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 0px;

    @media (max-width: 1350px) {
      font-size: 13px;
    }

    @media (max-width: 320px) {
      font-size: 14px;
    }
  }

  p {
    color: map-get($theme-colors, 'gray02');
    font-size: 12px;
    text-decoration: none;
    margin: 0;
  }

  .titles {
    text-align: center;
    color: map-get($theme-colors, 'primary');
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .subtitle {
    padding-left: 20px;
    padding-right: 20px;
  }

  .reliability-rate {
    word-wrap: normal;

    .veryLow {
      color: map-get($theme-colors, 'red10');
    }
    .low {
      color: map-get($theme-colors, 'red09');
    }
    .medium {
      color: map-get($theme-colors, 'yellow02');
    }
    .high {
      color: map-get($theme-colors, 'green06');
    }
    .veryHigh {
      color: map-get($theme-colors, 'green05');
    }
  }
}
</style>
